'use client';

import { Card } from '@app/app/_components/Card/Card';
import { CloseIcon } from '@app/app/_icons/CloseIcon';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';

interface Props {
    children: ReactNode;
    open?: boolean;
    onClose?: () => void;
    className?: string;
    cardClassName?: string;
    portal?: boolean;
    canClose?: boolean;
    closeOnBackdropClick?: boolean;
}

export const Modal = ({
    children,
    open = false,
    onClose,
    className,
    cardClassName,
    portal = true,
    canClose = true,
    closeOnBackdropClick = true,
}: Props) => {
    const dialogRef = useRef<HTMLDialogElement>(null);

    const [mounted, setMounted] = useState(false);

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();

                if (!canClose) {
                    return;
                }

                onClose?.();
            }
        },
        [canClose, onClose],
    );

    const handleBackdropClick = useCallback(
        (e: MouseEvent) => {
            if (e.target === dialogRef.current && canClose && closeOnBackdropClick) {
                onClose?.();
            }
        },
        [canClose, closeOnBackdropClick, onClose],
    );

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        if (!mounted) {
            return;
        }

        if (open) {
            dialogRef.current?.showModal();
        } else {
            dialogRef.current?.close();
        }
    }, [mounted, open]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    useEffect(() => {
        const dialog = dialogRef.current;

        dialog?.addEventListener('mousedown', handleBackdropClick);

        return () => {
            dialog?.removeEventListener('mousedown', handleBackdropClick);
        };
    }, [handleBackdropClick]);

    if (portal) {
        return mounted ? createPortal(renderDialog(), globalThis.document?.body) : null;
    }

    return renderDialog();

    function renderDialog() {
        return (
            <dialog
                ref={dialogRef}
                className={twMerge(
                    'bg-transparent backdrop:bg-black backdrop:opacity-45 backdrop:animate-in backdrop:fade-in',
                    className,
                )}>
                <Card className={twMerge('relative animate-in zoom-in md:max-w-dialog', cardClassName)}>
                    {canClose && (
                        <button className="absolute right-6 top-4 p-1" type="button" onClick={onClose}>
                            <CloseIcon className="size-4" />
                        </button>
                    )}

                    {open && children}
                </Card>
            </dialog>
        );
    }
};
